import { defineStore } from 'pinia';
import api from '@/api/api';
import applicationAPI from '@/api/application';
import {type ZApplication, type ZApplicant, type ZQueryResultApplications} from '@schemas/application.schemas';

export const useApplicationsStore = defineStore('applications', {
    state: () => ({
        /**
         * All applications for the current user
         */
        applications: [] as ZApplication[],
        /**
         * All applications for the current event
         */
        eventApplications: undefined as ZQueryResultApplications | undefined,
        currentApplication: null as ZApplication | null,
        eventApplicants: [] as ZApplicant[]
    }),
    getters: {
        getApplicationsForEvent: (state) => (eventId: string) =>
            state.applications.filter(app => app.eventId === eventId)
    },
    actions: {
        clearMyData(): void {
            this.applications = [];
            this.eventApplications = undefined;
            this.currentApplication = null;
            this.eventApplicants = [];
        },
        /**
         * Fetch applications for the current event
         * @param eventId
         * @param query
         */
        async fetchApplicationByEventId(eventId: string, query: Record<string, any> = {}): Promise<ZQueryResultApplications> {
            log.debug(`fetchApplicationByEventId(${eventId}) query=${JSON.stringify(query)})`);
            const found = this.getApplicationsForEvent(eventId);
            log.debug(`found applications: ${found.length}`);

            if (found.length > 0) {
                this.eventApplications = {
                    results: found,
                    count: found.length
                };
            } else {
                this.eventApplications = await api.getDocumentsWithCount<ZApplication>('applications/event/' + eventId, query);
                // Optionally push to `this.applications` to cache:

                // this.applications.push(...this.eventApplications);
            }
            return this.eventApplications;
        },
        async fetchApplication(applicationId: string): Promise<ZApplication> {
            log.debug(`fetchApplication(${applicationId})`);
            this.currentApplication = this.applications.find(app => app._id === applicationId) ||
                await api.getDocument<ZApplication>('applications', applicationId);
            return this.currentApplication;
        },
        async fetchApplications(query: Record<string, any> = {}): Promise<ZApplication[]> {
            this.applications = await api.getDocuments<ZApplication>('applications', query);
            // working
            //this.applications = await api.getDocuments<ZApplication>('applications/', {l: 5, sk: 0, q: JSON.stringify({status: 'pending'})});
            return this.applications;
        },
        async fetchApplicantsByEvent(eventId: string): Promise<ZApplicant[]> {
            log.debug(`fetchApplicantsByEvent(${eventId})`);
            this.eventApplicants = await api.getDocuments<ZApplicant>('applications/event/' + eventId + '/applicants');
            return this.eventApplicants;
        },
        async createApplication(eventId: string, applicants: ZApplicant[], message: string): Promise<ZApplication> {
            const newApplication = await applicationAPI.apply(eventId, applicants, message)
            this.applications.push(newApplication);
            return newApplication;
        },
        async updateStatus(applicationId: string, status: string, message: string = ""): Promise<ZApplication> {
            const updatedApplication = await applicationAPI.updateStatus(applicationId, status, message);
            //update applications
            this.applications = this.applications.map(app => app._id === updatedApplication._id ? updatedApplication : app);
            // update eventApplications
            if(this.eventApplications && this.eventApplications.results) {
                this.eventApplications.results = this.eventApplications.results.map(app => app._id === updatedApplication._id ? updatedApplication : app)
            }
            // update currentApplication
            // TODO use storeHelper
            if (this.currentApplication && this.currentApplication?._id === updatedApplication._id) {
                this.currentApplication.status = updatedApplication.status;
                this.currentApplication.updatedAt = updatedApplication.updatedAt;
            }
            return updatedApplication;
        },
        async updateApplication(application: Partial<ZApplication>): Promise<ZApplication> {
            const updatedApplication = await api.putDocument<ZApplication>('applications', application);
            this.applications = this.applications.map(app => app._id === updatedApplication._id ? updatedApplication : app);
            // TODO if currentApplication is updated, update it as well
            return updatedApplication;
        },
        async removeApplication(applicationId: string): Promise<void> {
            await api.deleteDocument<ZApplication>('applications', applicationId);
            this.applications = this.applications.filter(app => app._id !== applicationId);
        }
    }
});
